import { useState } from 'react';

import { TableCell, TableRow, useTheme } from '@/shared/ui';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import DateCell from '@/features/markets/components/shared/DateCell';
import ExpandCell from '@/features/markets/components/shared/ExpandCell';
import { getRowBackgroundColor } from '@/features/markets/components/shared/tableHelpers';
import { dataRowSx } from '@/features/markets/components/shared/tableStyles';
import ValueCell from '@/features/markets/components/shared/ValueCell';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import { getDiffColor } from '@/shared/lib/format/getDiffColor';

import CollapsedRow from './CollapsedRow';

import type { MultiGroupTableRowType, SymbolPropertyConfig } from '@/features/markets/types';

type Props = {
  row: MultiGroupTableRowType;
  index: number;
  columns: SymbolPropertyConfig[];
  isLoading?: boolean;
};

const MultiGroupTableRow = ({ row, index, columns, isLoading = false }: Props) => {
  const theme = useTheme();
  const { hasPermission } = usePermission();
  const isRestricted = !hasPermission(Permission.ViewMarketScreens);
  const { shortTitle, date, last, delta, bValues } = row;

  const hasAnyValue = Boolean(last) || Boolean(bValues && Object.keys(bValues).length > 0);
  const restrictedData = !hasAnyValue || isRestricted;
  const backgroundColor = getRowBackgroundColor(restrictedData, index, theme);

  const [open, setOpen] = useState(false);

  const lastDisplayValue = last ? formatNumberWithPrecision(last) : '';
  const deltaDisplayValue = delta ? formatNumberWithPrecision(delta) : '';

  const colSpan = 5 + columns.length;

  return (
    <>
      <TableRow
        key={shortTitle}
        className='markets-table-row'
        sx={{ ...dataRowSx, backgroundColor }}
      >
        <ExpandCell
          open={open}
          disabled={restrictedData}
          onToggle={() => {
            setOpen(!open);
          }}
        />
        <TableCell
          component='th'
          scope='row'
        >
          {shortTitle}
        </TableCell>
        <DateCell
          date={date}
          isLoading={isLoading}
        />
        <ValueCell
          isLoading={isLoading}
          hasValue={Boolean(last)}
          displayValue={lastDisplayValue}
          restricted={restrictedData}
        />
        <ValueCell
          isLoading={isLoading}
          hasValue={Boolean(delta)}
          displayValue={deltaDisplayValue}
          restricted={isRestricted}
          sx={{ color: getDiffColor(Number(delta)) }}
        />
        {columns.map(column => {
          const raw = bValues?.[column.name];
          const display = raw ? formatNumberWithPrecision(raw) : '';
          return (
            <ValueCell
              key={column.name}
              isLoading={isLoading}
              hasValue={Boolean(raw)}
              displayValue={display}
              restricted={restrictedData}
            />
          );
        })}
      </TableRow>
      <TableRow sx={{ backgroundColor }}>
        <CollapsedRow
          colSpan={colSpan}
          open={open}
          row={row}
        />
      </TableRow>
    </>
  );
};

export default MultiGroupTableRow;
